import { useLayoutEffect, useRef } from "react";

import { useMotionPolicy } from "./Motion";
import { listMovement, type ListPosition } from "./motionList";

interface ItemPosition extends ListPosition {
  node: HTMLElement;
}

/**
 * Mark stable rows with data-motion-id. layoutKey describes their ordered IDs;
 * scopeKey changes on navigation/filtering, so unrelated collections never FLIP.
 */
export function useMotionList({
  layoutKey,
  scopeKey,
  disabled = false,
}: {
  layoutKey: string;
  scopeKey: string;
  disabled?: boolean;
}) {
  const root = useRef<HTMLDivElement>(null);
  const previous = useRef<{
    scope: string;
    width: number;
    items: Map<string, ItemPosition>;
  }>(undefined);
  const animations = useRef(new Map<HTMLElement, Animation>());
  const policy = useMotionPolicy();

  useLayoutEffect(() => {
    const container = root.current;
    if (!container) return;
    // Read the current displacement before cancelling interrupted animations.
    const offsets = new Map<HTMLElement, ListPosition>();
    for (const [node, animation] of animations.current) {
      if (node.isConnected && animation.playState === "running") {
        const transform = getComputedStyle(node).transform;
        if (transform !== "none") {
          const matrix = new DOMMatrixReadOnly(transform);
          offsets.set(node, { x: matrix.m41, y: matrix.m42 });
        }
      }
      animation.cancel();
    }
    animations.current.clear();

    const bounds = container.getBoundingClientRect();
    const items = new Map<string, ItemPosition>();
    const visible = new Set<string>();
    const rows = Array.from(
      container.querySelectorAll<HTMLElement>("[data-motion-id]"),
    ).slice(0, 80);
    // Batch all layout reads before starting any animations.
    for (const node of rows) {
      const id = node.dataset.motionId;
      if (!id || items.has(id)) continue;
      const rect = node.getBoundingClientRect();
      items.set(id, {
        node,
        x: rect.left - bounds.left + container.scrollLeft,
        y: rect.top - bounds.top + container.scrollTop,
      });
      if (
        rect.width > 0 &&
        rect.height > 0 &&
        rect.bottom > Math.max(0, bounds.top) &&
        rect.top < Math.min(window.innerHeight, bounds.bottom) &&
        rect.right > Math.max(0, bounds.left) &&
        rect.left < Math.min(window.innerWidth, bounds.right)
      ) {
        visible.add(id);
      }
    }
    const before = previous.current;
    previous.current = { scope: scopeKey, width: bounds.width, items };
    if (
      disabled ||
      !policy.enabled ||
      !before ||
      !before.items.size ||
      before.scope !== scopeKey ||
      before.width !== bounds.width
    )
      return;

    let entered = 0;
    for (const [id, current] of items) {
      if (!visible.has(id) || !current.node.animate) continue;
      const old = before.items.get(id);
      let frames: Keyframe[] | undefined;
      if (old?.node === current.node && policy.distance > 0) {
        const offset = offsets.get(current.node);
        const delta = listMovement(
          { x: old.x + (offset?.x ?? 0), y: old.y + (offset?.y ?? 0) },
          current,
          Math.min(600, Math.max(bounds.width, bounds.height)),
        );
        if (delta)
          frames = [
            { transform: `translate(${delta.x}px, ${delta.y}px)` },
            { transform: "translate(0, 0)" },
          ];
      } else if (!old && entered < 6) {
        entered++;
        frames = [{ opacity: 0 }, { opacity: 1 }];
      }
      if (!frames) continue;
      const animation = current.node.animate(frames, {
        duration: policy.duration,
        easing: policy.easing,
      });
      animations.current.set(current.node, animation);
      animation.onfinish = () => {
        if (animations.current.get(current.node) === animation)
          animations.current.delete(current.node);
      };
    }
  }, [
    layoutKey,
    scopeKey,
    disabled,
    policy.enabled,
    policy.distance,
    policy.duration,
    policy.easing,
  ]);

  useLayoutEffect(
    () => () => {
      for (const animation of animations.current.values()) animation.cancel();
      animations.current.clear();
    },
    [],
  );
  return root;
}
